'use client'

import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdAssignment,
    MdAutoAwesome,
    MdBiotech,
    MdCheck,
    MdExpandLess,
    MdExpandMore,
    MdMedication,
    MdRefresh,
    MdWarning,
} from 'react-icons/md'
import type {
    ConsultationAiExtractedData,
    SuggestedExam,
} from '@/lib/api/consultations/consultation-api'
import { ClinicalInsights } from './clinical-insights'
import {
    addExamSuggestion,
    readExamSuggestions,
    removeExamSuggestion,
} from '@/lib/medical/exam-suggestions'

interface AiSummaryCardProps {
    summary?: string
    diagnosis?: string
    recommendations?: string
    followUp?: string
    extractedData?: ConsultationAiExtractedData
    /**
     * Paciente da consulta. Com ele, os exames sugeridos viram botões que montam a
     * lista do pedido — sem ele, a seção continua sendo só leitura (é o caso de
     * qualquer tela que exiba a análise fora do contexto de um paciente).
     */
    clientId?: number
    /** Consulta de origem, guardada junto da sugestão para rastreio. */
    consultationId?: number
    confidenceScore?: number
    warnings?: string[]
    model?: string
    processedAt?: string
    onRegenerate?: () => void
    isRegenerating?: boolean
    className?: string
}

interface ExtractedDataSectionProps {
    title: string
    items: string[]
    icon: React.ReactNode
    color: string
    /**
     * Quando presente, cada item ganha um botão que o marca para o pedido de
     * exames. Só a seção de exames usa — nas demais, o item é leitura.
     */
    onPick?: (item: string) => void
    /** Itens já marcados, para o botão mostrar o estado em vez de repetir a ação. */
    pickedItems?: string[]
}

const normalizeItem = (value: string): string =>
    (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()

function ExtractedDataSection({
    title,
    items,
    icon,
    color,
    onPick,
    pickedItems,
}: ExtractedDataSectionProps) {
    if (!items || items.length === 0) return null

    const isPicked = (item: string) =>
        (pickedItems ?? []).some(picked => normalizeItem(picked) === normalizeItem(item))

    return (
        <div className="mb-3">
            <div className={`flex items-center gap-2 text-sm font-medium mb-1.5 ${color}`}>
                {icon}
                {title}
            </div>

            {onPick ? (
                <ul className="space-y-1 pl-1">
                    {items.map((item, index) => {
                        const picked = isPicked(item)
                        return (
                            <li
                                key={index}
                                className="flex items-center justify-between gap-2 text-sm text-base-content/80"
                            >
                                <span className="min-w-0 break-words">{item}</span>
                                <button
                                    type="button"
                                    className={`btn btn-xs shrink-0 gap-1 ${
                                        picked ? 'btn-success btn-outline' : 'btn-outline'
                                    }`}
                                    onClick={() => onPick(item)}
                                    title={
                                        picked
                                            ? 'Remover da lista do pedido'
                                            : 'Marcar para o pedido de exames'
                                    }
                                >
                                    {picked ? (
                                        <>
                                            <MdCheck className="text-sm" />
                                            Adicionado
                                        </>
                                    ) : (
                                        <>
                                            <MdAdd className="text-sm" />
                                            Adicionar
                                        </>
                                    )}
                                </button>
                            </li>
                        )
                    })}
                </ul>
            ) : (
                <ul className="list-disc list-inside space-y-0.5 text-sm text-base-content/80 pl-1">
                    {items.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            )}
        </div>
    )
}

export function AiSummaryCard({
    summary,
    diagnosis,
    recommendations,
    followUp,
    extractedData,
    clientId,
    consultationId,
    confidenceScore,
    warnings,
    model,
    processedAt,
    onRegenerate,
    isRegenerating = false,
    className = '',
}: AiSummaryCardProps) {
    const [isExpanded, setIsExpanded] = useState(true)

    /**
     * Exames já marcados para o pedido, lidos do rascunho local. O estado existe
     * para o botão mostrar "Adicionado" — a verdade continua no `localStorage`, que
     * é o que o modal de pedido lê depois.
     */
    const [pickedExams, setPickedExams] = useState<string[]>([])

    useEffect(() => {
        if (!clientId) return
        setPickedExams(readExamSuggestions(clientId).map(item => item.term))
    }, [clientId])

    /**
     * Exame sugerido pela IA: guarda o `catalogId` que a ferramenta de busca
     * resolveu. É ele que faz o pedido marcar o item direto, sem a busca por texto
     * que sobrou para as análises geradas antes das ferramentas.
     */
    const toggleSuggestedExam = (exam: SuggestedExam) => {
        if (!clientId) return
        const alreadyPicked = pickedExams.some(
            picked => normalizeItem(picked) === normalizeItem(exam.name)
        )
        const next = alreadyPicked
            ? removeExamSuggestion(clientId, exam.name)
            : addExamSuggestion(clientId, exam.name, {
                  catalogId: exam.catalogId,
                  consultationId,
              })
        setPickedExams(next.map(item => item.term))
    }

    const toggleExamSuggestion = (term: string) => {
        if (!clientId) return
        const alreadyPicked = pickedExams.some(
            picked => normalizeItem(picked) === normalizeItem(term)
        )
        const next = alreadyPicked
            ? removeExamSuggestion(clientId, term)
            : addExamSuggestion(clientId, term, { consultationId })
        setPickedExams(next.map(item => item.term))
    }
    const [showExtracted, setShowExtracted] = useState(false)

    const confidenceColor =
        confidenceScore !== undefined
            ? confidenceScore >= 0.8
                ? 'text-success'
                : confidenceScore >= 0.5
                    ? 'text-warning'
                    : 'text-error'
            : ''

    const confidenceLabel =
        confidenceScore !== undefined
            ? confidenceScore >= 0.8
                ? 'Alta'
                : confidenceScore >= 0.5
                    ? 'Media'
                    : 'Baixa'
            : ''

    if (!summary && !isRegenerating) {
        return (
            <div className={`bg-base-200 rounded-xl p-6 ${className}`}>
                <div className="flex flex-col items-center justify-center gap-3 text-base-content/60">
                    <MdAutoAwesome className="w-12 h-12" />
                    <p className="text-sm text-center">
                        Análise IA não disponível.
                        <br />
                        Gere um resumo após a transcrição.
                    </p>
                    {onRegenerate && (
                        <button
                            onClick={onRegenerate}
                            className="btn btn-secondary btn-sm gap-2"
                        >
                            <MdAutoAwesome />
                            Gerar Resumo IA
                        </button>
                    )}
                </div>
            </div>
        )
    }

    if (isRegenerating) {
        return (
            <div className={`bg-base-200 rounded-xl p-6 ${className}`}>
                <div className="flex flex-col items-center justify-center gap-3">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                    <p className="text-sm text-base-content/60">
                        Gerando análise com IA...
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className={`bg-gradient-to-br from-secondary/5 to-secondary/5 rounded-xl overflow-hidden border border-secondary/10 ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-base-300/50">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="btn btn-ghost btn-sm btn-square"
                    >
                        {isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                    </button>
                    <div className="flex items-center gap-2">
                        <MdAutoAwesome className="text-secondary text-xl" />
                        <div>
                            <h3 className="font-semibold text-base-content">
                                Resumo por IA
                            </h3>
                            {processedAt && (
                                <p className="text-xs text-base-content/60">
                                    Gerado em{' '}
                                    {new Date(processedAt).toLocaleString('pt-BR')}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {confidenceScore !== undefined && (
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-base-content/60">
                                Confiança:
                            </span>
                            <span className={`text-xs font-medium ${confidenceColor}`}>
                                {confidenceLabel} ({Math.round(confidenceScore * 100)}%)
                            </span>
                        </div>
                    )}
                    {onRegenerate && (
                        <button
                            onClick={onRegenerate}
                            className="btn btn-ghost btn-sm gap-1"
                            title="Regenerar resumo"
                        >
                            <MdRefresh />
                            <span className="text-xs">Regenerar</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            {isExpanded && (
                <div className="p-4 space-y-4">
                    {/* Warnings */}
                    {warnings && warnings.length > 0 && (
                        <div className="alert alert-warning">
                            <MdWarning className="text-xl shrink-0" />
                            <div>
                                <h4 className="font-medium text-sm">Pontos de Atenção</h4>
                                <ul className="text-xs mt-1 space-y-0.5">
                                    {warnings.map((warning, index) => (
                                        <li key={index}>- {warning}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* Summary */}
                    {summary && (
                        <div>
                            <h4 className="text-sm font-medium text-base-content/70 mb-1">
                                Resumo
                            </h4>
                            <p className="text-sm text-base-content leading-relaxed">
                                {summary}
                            </p>
                        </div>
                    )}

                    {/* Diagnosis */}
                    {diagnosis && (
                        <div>
                            <h4 className="text-sm font-medium text-base-content/70 mb-1">
                                Diagnóstico / Hipóteses
                            </h4>
                            <p className="text-sm text-base-content leading-relaxed">
                                {diagnosis}
                            </p>
                        </div>
                    )}

                    {/* Recommendations */}
                    {recommendations && (
                        <div>
                            <h4 className="text-sm font-medium text-base-content/70 mb-1">
                                Recomendações
                            </h4>
                            <p className="text-sm text-base-content leading-relaxed">
                                {recommendations}
                            </p>
                        </div>
                    )}

                    {/* Follow-up */}
                    {followUp && (
                        <div>
                            <h4 className="text-sm font-medium text-base-content/70 mb-1">
                                Retorno / Acompanhamento
                            </h4>
                            <p className="text-sm text-base-content leading-relaxed">
                                {followUp}
                            </p>
                        </div>
                    )}

                    {/* Leitura clínica: hipóteses, exames sugeridos e sinais de
                        alarme. Fica ANTES dos dados extraídos (que ficam atrás de um
                        toggle) porque é a conclusão, não o material bruto — e o sinal
                        de alarme não pode depender de alguém abrir uma seção. */}
                    {extractedData && (
                        <ClinicalInsights
                            hypotheses={extractedData.diagnosticHypotheses}
                            suggestedExams={extractedData.suggestedExams}
                            redFlags={extractedData.redFlags}
                            onPickExam={clientId ? toggleSuggestedExam : undefined}
                            pickedExams={pickedExams}
                        />
                    )}

                    {/* Extracted Data Toggle */}
                    {extractedData && (
                        <div className="pt-2 border-t border-base-300/50">
                            <button
                                onClick={() => setShowExtracted(!showExtracted)}
                                className="flex items-center gap-2 text-sm font-medium text-secondary hover:underline"
                            >
                                {showExtracted ? <MdExpandLess /> : <MdExpandMore />}
                                Dados Extraídos
                            </button>

                            {showExtracted && (
                                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <ExtractedDataSection
                                            title="Queixas Principais"
                                            items={extractedData.mainComplaints}
                                            icon={<MdAssignment />}
                                            color="text-info"
                                        />
                                        <ExtractedDataSection
                                            title="Sintomas"
                                            items={extractedData.symptoms}
                                            icon={<MdAssignment />}
                                            color="text-warning"
                                        />
                                        <ExtractedDataSection
                                            title="Achados Exame Físico"
                                            items={extractedData.physicalExamFindings}
                                            icon={<MdAssignment />}
                                            color="text-secondary"
                                        />
                                        <ExtractedDataSection
                                            title="Alergias"
                                            items={extractedData.allergiesMentioned}
                                            icon={<MdWarning />}
                                            color="text-error"
                                        />
                                        <ExtractedDataSection
                                            title="Fatores de Risco"
                                            items={extractedData.riskFactors}
                                            icon={<MdWarning />}
                                            color="text-error"
                                        />
                                    </div>
                                    <div>
                                        <ExtractedDataSection
                                            title="Medicamentos Prescritos"
                                            items={extractedData.medicationsPrescribed}
                                            icon={<MdMedication />}
                                            color="text-success"
                                        />
                                        <ExtractedDataSection
                                            title="Exames Solicitados"
                                            items={extractedData.examsRequested}
                                            icon={<MdBiotech />}
                                            color="text-secondary"
                                            onPick={
                                                clientId ? toggleExamSuggestion : undefined
                                            }
                                            pickedItems={pickedExams}
                                        />
                                        <ExtractedDataSection
                                            title="Procedimentos"
                                            items={extractedData.orders}
                                            icon={<MdAssignment />}
                                            color="text-accent"
                                        />
                                        <ExtractedDataSection
                                            title="Perguntas do Paciente"
                                            items={extractedData.patientQuestions}
                                            icon={<MdAssignment />}
                                            color="text-base-content/70"
                                        />
                                        <ExtractedDataSection
                                            title="Instruções do Médico"
                                            items={extractedData.doctorInstructions}
                                            icon={<MdAssignment />}
                                            color="text-info"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}


                </div>
            )}
        </div>
    )
}
